import { useState } from 'react';
import { Camera } from 'lucide-react';
import type { HandoverViewDto } from '@eombor/contracts';
import { Badge, Button, EmptyState, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { AuthImage } from '../../../shared/ui/AuthImage.js';
import { GeoLink } from '../../../shared/ui/GeoLink.js';
import { PhotoLightbox, type LightboxPhoto } from '../../../shared/ui/PhotoLightbox.js';
import { useHandovers } from '../model/handover.queries.js';

/** Sana va vaqt: "03.08.2026 11:00" — vaqt chizig'i bilan bir xil shakl. */
const pad = (value: number): string => String(value).padStart(2, '0');

const formatMoment = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

interface ActProps {
  act: HandoverViewDto;
  /** Ko'ruvchidagi UMUMIY ro'yxatdagi birinchi rasmning o'rni. */
  offset: number;
  onOpen: (index: number) => void;
}

/**
 * Bitta dalolatnoma: kim, kimga, qachon va QANDAY RASM bilan.
 *
 * Rasm yo'li javob bilan birga keladi (`files/<uuid>`), baytlarni esa
 * `AuthImage` klient orqali oladi — token sarlavhada yuradi va uni `<img>`
 * yubora olmaydi.
 */
const Act = ({ act, offset, onOpen }: ActProps) => (
  <li className="flex flex-col gap-3 border-b border-border pb-4 last:border-0 last:pb-0">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Badge tone={act.kind === 'ISSUE' ? 'accent' : 'success'}>
        {t(act.kind === 'ISSUE' ? 'handover.actIssue' : 'handover.actReceipt')}
      </Badge>
      <span className="tabular text-xs text-text-faint">{formatMoment(act.createdAt)}</span>
    </div>

    <dl className="grid gap-2 sm:grid-cols-2">
      {act.receiverName === null ? null : (
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-text-faint">{t('handover.actReceiver')}</dt>
          <dd className="text-sm text-text">{act.receiverName}</dd>
        </div>
      )}
      {act.gps === null ? null : (
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-text-faint">{t('handover.actGps')}</dt>
          <dd className="text-sm">
            <GeoLink gps={act.gps} />
          </dd>
        </div>
      )}
    </dl>

    {act.comment === null ? null : (
      <p className="rounded-[var(--radius-sm)] bg-surface-2 px-3 py-2 text-sm text-text-muted">
        {act.comment}
      </p>
    )}

    {act.photos.length === 0 ? (
      <p className="text-xs text-text-faint">{t('handover.actNoPhotos')}</p>
    ) : (
      <ul className="grid grid-cols-3 gap-2">
        {act.photos.map((photo, index) => (
          <li key={photo.fileId}>
            {/*
              Rasm ILOVA ICHIDA ochiladi. Ilgari u yangi oynada ochilardi va
              izohda "tekshiruvchi baribir alohida oynada ko'radi" deb
              yozilgan edi — amalda esa brauzer rasmni ochmay YUKLAB OLARDI
              (havolada `attachment` turardi), telefonda esa hujjatga
              qaytish uchun orqaga bosish kerak bo'lardi.
            */}
            <button
              type="button"
              onClick={() => onOpen(offset + index)}
              title={t('handover.actPhotoOpen')}
              className={cn(
                'block w-full overflow-hidden rounded-[var(--radius-md)] border border-border',
                'transition-[border-color] duration-[var(--motion-fast)]',
                'hover:border-accent focus-visible:border-accent focus-visible:outline-none',
              )}
            >
              <AuthImage
                src={photo.url}
                alt={t('handover.actPhotoAlt', { index: index + 1 })}
                className="aspect-square w-full object-cover"
              />
            </button>
          </li>
        ))}
      </ul>
    )}
  </li>
);

export interface HandoverActsProps {
  requisitionId: string;
}

/**
 * ARIZANING DALOLATNOMALARI — foto-hisobot kartochkada.
 *
 * Dalolatnoma alohida so'rov bilan olinadi (talabnoma kartochkasi bilan
 * birga emas): u O'ZGARMAS hujjat va uzoq keshlanadi, talabnoma esa har
 * qadamda o'zgaradi. Bittasiga qo'shilsa, ikkalasi ham eng qisqa muddat
 * bo'yicha yangilanardi.
 */
export const HandoverActs = ({ requisitionId }: HandoverActsProps) => {
  const acts = useHandovers(requisitionId);
  const [opened, setOpened] = useState<number | null>(null);

  if (acts.isPending) return <Skeleton className="h-32 w-full" />;

  if (acts.isError) {
    return (
      <EmptyState
        title={t('handover.actsError')}
        action={
          <Button variant="secondary" onClick={() => void acts.refetch()}>
            {t('handover.retry')}
          </Button>
        }
      />
    );
  }

  if (acts.data.length === 0) {
    return (
      <EmptyState
        icon={<Camera size={20} aria-hidden />}
        title={t('handover.actsEmpty')}
        description={t('handover.actsEmptyHint')}
      />
    );
  }

  /*
   * KO'RUVCHI uchun rasmlar BITTA ro'yxatga yig'iladi.
   *
   * Sabab: o'q tugmalari berish va qabul rasmlari orasidan uzluksiz o'tishi
   * kerak. Har dalolatnoma o'z ko'ruvchisini ochsa, tekshiruvchi "berilgan"
   * bilan "olingan" ni solishtirish uchun oynani yopib, ikkinchisini
   * ochishga majbur bo'lardi — solishtiruvning butun ma'nosi esa shunda.
   */
  const photos: LightboxPhoto[] = acts.data.flatMap((act) =>
    act.photos.map((photo) => ({
      id: photo.fileId,
      url: photo.url,
      gps: photo.gps,
      caption: `${t(act.kind === 'ISSUE' ? 'handover.actIssue' : 'handover.actReceipt')} · ${formatMoment(act.createdAt)}`,
    })),
  );

  /** Dalolatnomaning birinchi rasmi umumiy ro'yxatda nechanchi o'rinda. */
  let cursor = 0;
  const offsets = acts.data.map((act) => {
    const start = cursor;
    cursor += act.photos.length;
    return start;
  });

  return (
    <>
      <ul className="flex flex-col gap-4">
        {acts.data.map((act, index) => (
          <Act key={act.id} act={act} offset={offsets[index] ?? 0} onOpen={setOpened} />
        ))}
      </ul>

      <PhotoLightbox
        photos={photos}
        index={opened}
        onIndexChange={setOpened}
        onClose={() => setOpened(null)}
      />
    </>
  );
};
